import {useI18n} from "@project/i18n/react";

import {componentMessages} from "../../../components.messages";

interface DateCellProps {
  /** ISO 8601 date string (e.g. "2024-01-15T10:30:00.000Z") */
  date: string;
}

/**
 * DateCell — generic table cell for displaying ISO 8601 date strings.
 *
 * Internalizes the date formatting logic so column definitions remain clean.
 * Renders an accessible `<time>` element with the machine-readable `dateTime`
 * attribute, and falls back to an em-dash ("—") for invalid/empty dates.
 *
 * @example
 * ```tsx
 * columnHelper.accessor("createdAt", {
 *   cell: (info) => <DateCell date={info.getValue()} />,
 *   header: "Created",
 * })
 * ```
 */
export function DateCell({date}: DateCellProps) {
  const {intl, t} = useI18n();
  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return <span aria-label={t(componentMessages.unknownDate)}>—</span>;
  }

  return (
    <time dateTime={date}>
      {intl.formatDate(parsedDate, {day: "numeric", month: "short", year: "numeric"})}
    </time>
  );
}
